'use client';

import { useMemo } from 'react';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { useDateFormatter } from '@terra/ui-formatters';
import { useTranslations } from 'next-intl';
import { Badge } from '../../../components/badge';
import { BodyDefault, MetaDefault } from '../../../components/typography';
import { CustomDrawer } from '../../custom-drawer/CustomDrawer';
import { EntityIcon } from '../../icons/EntityIcon';
import { humanizeSnakeCase } from './humanize-snake-case';
import type { ScopeEvidence } from './scope-types';

type EvidenceDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  evidences: ScopeEvidence[];
};

const toMillis = (value: ScopeEvidence['timestamp']): number => {
  if (!value) {
    return 0;
  }

  const time = (value instanceof Date ? value : new Date(value)).getTime();

  return Number.isFinite(time) ? time : 0;
};

export function EvidenceDrawer({
  isOpen,
  onClose,
  title,
  evidences,
}: EvidenceDrawerProps) {
  const t = useTranslations('designSystem.networkSettings.externalScope');
  const dateFormatter = useDateFormatter();

  const sortedEvidences = useMemo(
    () =>
      [...evidences].sort(
        (left, right) => toMillis(right.timestamp) - toMillis(left.timestamp),
      ),
    [evidences],
  );

  return (
    <CustomDrawer
      isOpen={isOpen}
      onClose={onClose}
      header={{
        topic: t('evidence'),
        title,
        iconConfig: {
          iconElement: (
            <EntityIcon type="asset" appearance="highlighted" density="large" />
          ),
        },
      }}
      tabs={[{ value: 'overview', label: t('evidence') }]}
      selectedTab="overview"
      onTabChange={() => undefined}
    >
      <VStack gap={3} alignItems="stretch">
        {sortedEvidences.length === 0 ? (
          <Box py={6}>
            <BodyDefault color="fg.neutral.muted">
              {t('noEvidenceCollected')}
            </BodyDefault>
          </Box>
        ) : (
          sortedEvidences.map((evidence, index) => (
            <VStack
              key={`${evidence.evidenceType}-${evidence.text}-${index}`}
              gap={2}
              alignItems="stretch"
              borderWidth="1px"
              borderColor="border.neutral.subtle"
              borderRadius="md"
              p={3}
            >
              <HStack justifyContent="space-between" alignItems="center">
                <Badge variant="outline" size="sm" colorPalette="gray">
                  {humanizeSnakeCase(evidence.evidenceType)}
                </Badge>
                {evidence.timestamp && (
                  <MetaDefault color="fg.neutral.muted">
                    {dateFormatter.format(new Date(evidence.timestamp))}
                  </MetaDefault>
                )}
              </HStack>
              <BodyDefault color="fg.neutral.default">
                {evidence.text}
              </BodyDefault>
            </VStack>
          ))
        )}
      </VStack>
    </CustomDrawer>
  );
}
